import type {
  Cluster,
  Fleet,
  FleetOperation,
  FleetOperationChild,
  FleetOperationEvent,
  ResolvedFleet,
} from "~/types";
import { CONCLUDED_FLEET_STATUSES } from "~/types";

import { parseFleetTag } from "./rollout";

/** Rollout-group ordering from the fleet: only rollout-key tag selectors
 * position groups; other keys don't correspond to rollout buckets. */
export function fleetTagOrder(fleet: Fleet | null): string[] | undefined {
  return fleet?.selectors.flatMap((selector) => {
    if (!("tag" in selector)) return [];
    const parsed = parseFleetTag(selector.tag);
    return parsed?.key === "rollout" ? [parsed.value] : [];
  });
}

export const DEFAULT_FLEET = "default";

export interface Wave {
  label?: string;
  clusters: string[];
}

/** Minimal version of the waves that only include clusters specified in clusterIds. */
function narrowWaves(waves: Wave[], clusterIds: Set<string>): Wave[] {
  return waves
    .map((wave) => ({
      ...wave,
      clusters: wave.clusters.filter((id) => clusterIds.has(id)),
    }))
    .filter((wave) => wave.clusters.length > 0);
}

/** Orders `clusterIds` by a fleet's waves. Clusters the fleet doesn't cover
 * trail the rest rather than dropping out of the operation. */
export function orderByFleet(waves: Wave[], clusterIds: string[]): Wave[] {
  const ordered = narrowWaves(waves, new Set(clusterIds));
  const placed = new Set(ordered.flatMap((wave) => wave.clusters));
  const rest = clusterIds.filter((id) => !placed.has(id));
  return rest.length > 0
    ? [...ordered, { label: "not in fleet", clusters: rest }]
    : ordered;
}

export function activeFleetOperations(
  operations: FleetOperation[],
): FleetOperation[] {
  return operations.filter(
    (operation) => !CONCLUDED_FLEET_STATUSES.includes(operation.status),
  );
}

/** How long an operation has taken; a concluded one stops at its last update. */
export function fleetOperationDuration(
  operation: FleetOperation,
  now: number,
): number {
  const end = CONCLUDED_FLEET_STATUSES.includes(operation.status)
    ? operation.updated_at.getTime()
    : now;
  return end - operation.created_at.getTime();
}

/** First spawn to last settle, or to now while any child is unsettled. */
export function childrenDuration(
  children: FleetOperationChild[],
  now: number,
): number | null {
  if (children.length === 0) return null;
  const start = Math.min(
    ...children.map((child) => child.started_at.getTime()),
  );
  let end = start;
  for (const child of children) {
    if (!child.ended_at) return now - start;
    end = Math.max(end, child.ended_at.getTime());
  }
  return end - start;
}

export function clustersOnCommit(clusters: Cluster[], sha: string): Cluster[] {
  return clusters.filter(
    (cluster) => cluster.git_details?.current_commit?.sha === sha,
  );
}

export function waveClusterCount(fleet: ResolvedFleet | null): number {
  return (
    fleet?.waves.reduce((count, wave) => count + wave.clusters.length, 0) ?? 0
  );
}

/** An event's reason and actor as one line. */
export function describeFleetEvent(event: FleetOperationEvent): string {
  return [event.reason, `by ${event.actor}`].filter(Boolean).join(" ");
}

/** Why a paused operation is paused. Ops paused before history was recorded
 * fall back to the failed children of the current wave. */
export function pauseReason(
  operation: FleetOperation,
  events: FleetOperationEvent[],
  children: FleetOperationChild[],
): { text: string; at: Date | null } | null {
  if (operation.status !== "PAUSED") return null;
  const pause = [...events].reverse().find((e) => e.status === "PAUSED");
  if (pause) {
    return { text: describeFleetEvent(pause), at: pause.created_at };
  }
  const { currentWave, totalWaves } = fleetOperationProgress(
    operation,
    children,
  );
  const failed = children
    .filter(
      (child) =>
        child.wave === currentWave - 1 &&
        (child.status === "FAILURE" || child.status === "CANCELLING"),
    )
    .map((child) => child.cluster_id);
  if (failed.length > 0) {
    return {
      text: `wave ${currentWave}/${totalWaves}: ${failed.join(", ")} failed or was cancelled`,
      at: null,
    };
  }
  return { text: "reason not recorded", at: null };
}

export function fleetOperationProgress(
  operation: FleetOperation,
  children: FleetOperationChild[],
) {
  return {
    done: children.filter((child) => child.ended_at).length,
    total: operation.targets.reduce((count, wave) => count + wave.length, 0),
    currentWave:
      children.length > 0
        ? Math.max(...children.map((child) => child.wave)) + 1
        : 1,
    totalWaves: operation.targets.length,
  };
}
